import { formatarMoeda, formatarData } from '../lib/formatos'
import type { DadosPedidoPdf, DadosSubPedidoPdf } from './pdfDados'
import { quebrasPorCondicao, marcasParaCliente, marcasParaFabrica } from './agrupamento'
import { rotuloDoMotivo } from './ajustes'
import { estagioDoPedido } from './estagio'
import { textoEnderecoEntrega } from '../clientes/enderecoEntrega'

// O MESMO documento do PdfPedido.tsx, desenhado em HTML para ela conferir no
// celular antes de mandar. Vive aqui, do lado dele, porque os dois nao podem
// divergir: previa que mente e pior que previa nenhuma -- ela para de olhar.
//
// ⚠⚠ REGRA DESTE ARQUIVO: o LAYOUT muda (grade de 6 colunas nao se le em
// celular), as STRINGS nao. Cada texto e escrito com a mesma expressao do
// PdfPedido.tsx, na mesma ordem. E o que faz o teste de nao-divergencia ser
// possivel -- e o que faz ele valer alguma coisa.
//
// ⚠ SEM HOOK. O vitest deste projeto roda em environment 'node' e o jeito de
// testar componente aqui e chamar a funcao e descer a arvore (coletarTextos).
// Um useState aqui dentro derruba o teste com "Invalid hook call".

function Cabecalho({ titulo, dados }: { titulo: string; dados: DadosPedidoPdf }) {
  // Mesma expressao de PdfPedido.tsx, string unica -- telefone (09/09) so
  // entra quando cadastrado.
  const { nome, email, telefone } = dados.representante
  const linhaRepresentante =
    `Representante: ${nome} — ${email}`
    + `${telefone ? ` — ${telefone}` : ''}`
    + ` — ${formatarData(new Date(dados.pedido.criado_em))}`
  return (
    <div className="border-b-2 border-teal-700 pb-2">
      <h2 className="text-base font-bold text-slate-800">{titulo}</h2>
      <span className="block text-[11px] text-slate-500">{linhaRepresentante}</span>
    </div>
  )
}

function BlocoCliente({
  dados, codigoCliente,
}: { dados: DadosPedidoPdf; codigoCliente?: string | null }) {
  const { cliente } = dados
  return (
    <div className="bg-white rounded-xl p-3 space-y-1">
      <span className="block text-[10px] uppercase text-slate-500">Cliente</span>
      <span className="block text-sm text-slate-800">
        {cliente.razao_social}
        {cliente.nome_fantasia ? ` (${cliente.nome_fantasia})` : ''}
        {codigoCliente ? ` — Código: ${codigoCliente}` : ''}
      </span>
      {cliente.cnpj_cpf && (
        <span className="block text-sm text-slate-800">CNPJ/CPF: {cliente.cnpj_cpf}</span>
      )}
      <span className="block text-sm text-slate-800">
        {[cliente.endereco, cliente.bairro, cliente.municipio, cliente.uf]
          .filter(Boolean)
          .join(', ')}
      </span>
      {/* Igual ao PDF: entrega so aparece quando difere da do cliente. */}
      {textoEnderecoEntrega(cliente) && (
        <span className="block text-sm text-slate-800">Entrega: {textoEnderecoEntrega(cliente)}</span>
      )}
    </div>
  )
}

// A grade do PDF vira uma linha por item, que e o que cabe no celular. As
// celulas continuam sendo as mesmas seis strings.
function TabelaItens({ itens }: { itens: DadosSubPedidoPdf['itens'] }) {
  return (
    <div className="divide-y divide-slate-100">
      {itens.map(({ item, produto, quantidadeTexto, totalVendido, devolucoes }) => (
        <div key={item.id} className="p-3 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-slate-800">
              {produto.codigo} {produto.descricao}
            </span>
            <span className="text-slate-800 whitespace-nowrap">{formatarMoeda(totalVendido)}</span>
          </div>
          <span className="block text-xs text-slate-500">
            {quantidadeTexto} × {formatarMoeda(item.preco_unitario)}{' '}
            {item.desconto_pct > 0 ? `${item.desconto_pct}%` : '—'}
          </span>
          {devolucoes.map((devolucao, indice) => (
            <div
              key={`${item.id}-dev-${indice}`}
              className="flex justify-between gap-2 text-xs text-amber-700"
            >
              <span>
                {`Devolução de ${devolucao.quantidade} · ${rotuloDoMotivo(devolucao.motivo)}`}
              </span>
              <span className="whitespace-nowrap">{`−${devolucao.quantidade}`}</span>
              <span className="whitespace-nowrap">{`−${formatarMoeda(devolucao.valor)}`}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ⚠ Mesmas regras do PDF, inclusive as do bloco cancelado (spec 2026-08-04):
// Pagamento vira CANCELADO e Frete/Previsao SOMEM. Deduzir isso aqui de outro
// jeito criaria uma segunda fonte de verdade que um dia discorda da faixa.
function BlocoCondicoes({
  sub, mostrarTabela = false, cancelada = false,
}: { sub: DadosSubPedidoPdf; mostrarTabela?: boolean; cancelada?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50">
      <div>
        <span className="block text-[10px] uppercase text-slate-500">Prazo</span>
        <span className="text-xs text-slate-800">
          {cancelada ? 'CANCELADO' : (sub.condicaoPagamento ?? '—')}
        </span>
      </div>
      {!cancelada && sub.subPedido.forma_pagamento && (
        <div>
          <span className="block text-[10px] uppercase text-slate-500">Forma de pagamento</span>
          <span className="text-xs text-slate-800">{sub.subPedido.forma_pagamento}</span>
        </div>
      )}
      {mostrarTabela && sub.tabelaPreco && (
        <div>
          <span className="block text-[10px] uppercase text-slate-500">Tabela</span>
          <span className="text-xs text-slate-800">{sub.tabelaPreco}</span>
        </div>
      )}
      {!cancelada && (
        <div>
          <span className="block text-[10px] uppercase text-slate-500">Frete</span>
          <span className="text-xs text-slate-800">
            {sub.subPedido.frete_tipo ?? '—'}
            {sub.subPedido.transportadora ? ` — ${sub.subPedido.transportadora}` : ''}
          </span>
        </div>
      )}
      {!cancelada && (
        <div>
          <span className="block text-[10px] uppercase text-slate-500">Previsão de entrega</span>
          <span className="text-xs text-slate-800">
            {sub.subPedido.previsao_entrega
              ? formatarData(new Date(`${sub.subPedido.previsao_entrega}T12:00:00`))
              : '—'}
          </span>
        </div>
      )}
    </div>
  )
}

// ⚠ Percorre marca.tabelas, igual ao DocumentoSubPedido -- NAO quebrasPorCondicao.
// O documento da fabrica mostra uma secao por tabela, com subtotal quando ha mais
// de uma; agrupar por condicao aqui fundiria duas tabelas de mesma condicao e
// faria sumir um subtotal que o PDF imprime.
//
// indice e o da lista de marcasParaFabrica -- o MESMO que DocumentoSubPedido
// usa. Quem resolve letra -> indice e acharAlvoDoEnvio, uma porta so.
export function PreviaSubPedido({ dados, indice }: { dados: DadosPedidoPdf; indice: number }) {
  const marca = marcasParaFabrica(dados)[indice]
  if (!marca) return <p className="text-sm text-slate-500">Documento não encontrado.</p>
  const varias = marca.tabelas.length > 1
  return (
    <div className="space-y-3" data-testid="previa-conteudo">
      <Cabecalho titulo={`Pedido ${marca.numero} — ${marca.representada.nome}`} dados={dados} />
      <BlocoCliente dados={dados} codigoCliente={marca.tabelas[0].codigoCliente} />
      {marca.tabelas.map((sub) => (
        <div key={sub.subPedido.id} className="bg-white rounded-xl overflow-hidden">
          <TabelaItens itens={sub.itens} />
          <BlocoCondicoes sub={sub} mostrarTabela />
          {varias && (
            <div className="px-3 py-2 text-sm text-right text-slate-800">
              Subtotal: {formatarMoeda(sub.subPedido.valor_total)}
            </div>
          )}
        </div>
      ))}
      <div className="bg-teal-700 text-white rounded-xl px-3 py-2 text-sm font-bold text-right">
        Total: {formatarMoeda(marca.total)}
      </div>
    </div>
  )
}

export function PreviaConsolidado({ dados }: { dados: DadosPedidoPdf }) {
  const marcas = marcasParaCliente(dados)
  const tudoCancelado = estagioDoPedido(dados.subPedidos.map((s) => s.subPedido)) === 'cancelado'
  const motivos = [
    ...new Set(
      dados.subPedidos
        .filter((s) => s.subPedido.status === 'cancelado')
        .map((s) => s.subPedido.motivo_cancelamento)
        .filter((m): m is string => !!m),
    ),
  ]
  const motivoUnico = motivos.length === 1 ? rotuloDoMotivo(motivos[0]) : null

  return (
    <div className="space-y-3" data-testid="previa-conteudo">
      {tudoCancelado && (
        <div className="bg-red-600 text-white rounded-xl px-3 py-2 text-center font-bold">
          CANCELADO
        </div>
      )}
      <Cabecalho titulo={`Resumo do pedido ${dados.pedido.numero}`} dados={dados} />
      {tudoCancelado && (
        <div className="bg-red-100 border border-red-300 rounded-xl px-3 py-2 text-center">
          <span className="block font-bold text-red-800">PEDIDO CANCELADO</span>
          {motivoUnico && <span className="block text-sm text-red-700">{motivoUnico}</span>}
        </div>
      )}
      <BlocoCliente dados={dados} />
      {marcas.map((marca) => {
        const quebras = quebrasPorCondicao(marca)
        const variosBlocosEUmCaiu = quebras.length > 1 && quebras.some((q) => q.cancelada)
        return (
          <div key={marca.representadaId} className="space-y-2">
            <span className="block text-sm font-bold text-slate-700">
              {marca.numero} — {marca.representada.nome}
            </span>
            {quebras.map((quebra, indice) => (
              <div key={indice} className="bg-white rounded-xl overflow-hidden">
                {quebra.cancelada && (
                  <div className="bg-red-100 text-red-800 px-3 py-2 text-xs font-bold">
                    {[
                      'CANCELADO',
                      quebra.condicoes.subPedido.motivo_cancelamento
                        ? rotuloDoMotivo(quebra.condicoes.subPedido.motivo_cancelamento)
                        : null,
                      quebra.condicoes.dataCancelamento
                        ? formatarData(new Date(quebra.condicoes.dataCancelamento))
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                )}
                <TabelaItens itens={quebra.itens} />
                <BlocoCondicoes sub={quebra.condicoes} cancelada={quebra.cancelada} />
                {variosBlocosEUmCaiu && (
                  <div className="px-3 py-2 flex justify-end gap-2 text-sm">
                    <span
                      className={quebra.cancelada ? 'line-through text-slate-400' : 'text-slate-800'}
                    >
                      Subtotal: {formatarMoeda(quebra.total)}
                    </span>
                    {quebra.cancelada && (
                      <span className="text-xs text-slate-500">(não cobrado)</span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* ⚠ Marca inteira cancelada mostra o `total` (o riscado e quem diz
                QUANTO caiu; riscar zero nao informa nada). Marca viva mostra o
                totalCobravel, senao o subtotal contradiz o TOTAL A PAGAR -- foi
                exatamente o Critico do ciclo do PDF cancelado. */}
            <div className="px-3 flex justify-end gap-2 text-sm">
              <span className={marca.cancelada ? 'line-through text-slate-400' : 'text-slate-800'}>
                Subtotal: {formatarMoeda(marca.cancelada ? marca.total : marca.totalCobravel)}
              </span>
              {marca.cancelada && <span className="text-xs text-slate-500">(não cobrado)</span>}
            </div>
          </div>
        )
      })}
      <div className="bg-teal-700 text-white rounded-xl px-3 py-2 text-sm font-bold text-right">
        TOTAL A PAGAR: {formatarMoeda(dados.totalGeral)}
      </div>
    </div>
  )
}
